import { Component, Inject, OnInit } from "@angular/core";
import { filter, startWith, switchMap, shareReplay, map } from "rxjs/operators";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { USER_IS_AT_LEAST_ADMIN, UserService } from "@/_services";
import { AccountService } from "@/_services/account.service";
import { combineLatest, Observable, of } from "rxjs";
import { AssessmentDto, isAsapAccount } from "@/_models";
import { MenuService } from "@/_services/menu.service";
import { TabItem } from "@/_models/menu-item";
import { NewReportSidebarService } from "@/_services/new-report-sidebar.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@Component({
    selector: "app-assessment",
    templateUrl: "./assessment.component.html",
    styleUrls: ["./assessment.component.scss"],
})
@UntilDestroy()
export class AssessmentComponent implements OnInit {
    assessment$: Observable<AssessmentDto>;
    isEdited$: Observable<boolean>;
    hasAccess$: Observable<boolean>;
    navTabs$: Observable<TabItem[]>;
    currentTab$: Observable<TabItem>;

    newReportSidebarVisible = false;
    selectedAssessment: AssessmentDto | null = null;
    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private menuService: MenuService,
        private userService: UserService,
        private accountService: AccountService,
        private newReportSidebarService: NewReportSidebarService,
        @Inject(USER_IS_AT_LEAST_ADMIN)
        public isAtLeastAdmin$: Observable<boolean>,
    ) {}

    get returnToUrl(): string {
        return this.router.url;
    }

    ngOnInit() {
        this.isEdited$ = this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            startWith(this.router),
            map(() => this.router.url.includes("/edit")),
        );

        this.assessment$ = this.activatedRoute.data.pipe(
            switchMap((data) => data.form as Observable<AssessmentDto>),
        );

        // Check access based on form type and user account
        this.hasAccess$ = combineLatest([
            this.assessment$,
            this.userService.getCurrentUserAsync(),
        ]).pipe(
            switchMap(([form, user]) => {
                const isAsap = isAsapAccount(user?.account);
                // ASAP forms should always be accessible regardless of features
                if (form.title === "ASAP Report") {
                    return of(true);
                } else if (isAsap && form.title === "Risk Report") {
                    return of(false);
                } else {
                    // Use original feature validation for all other forms
                    return this.accountService.isFeatureEnabled(
                        form.formTypeSettings.requiresFeature,
                    );
                }
            }),
            shareReplay(),
        );

        this.navTabs$ = combineLatest([
            this.activatedRoute.params,
            this.isEdited$,
        ]).pipe(
            switchMap(([params, isEdited]) => {
                if (isEdited) {
                    return this.menuService.createAssessmentEditorMenu(
                        params["assessmentId"],
                    );
                }
                return this.menuService.createAssessmentViewMenu(
                    params["assessmentId"],
                );
            }),
            shareReplay(),
        );

        this.currentTab$ = this.menuService
            .getCurrentTab(this.navTabs$)
            .pipe(shareReplay());

        this.newReportSidebarService.openRequest$
            .pipe(untilDestroyed(this))
            .subscribe((assessment) => this.openNewReport(assessment));
    }

    openNewReport(assessment: AssessmentDto): void {
        if (!assessment?._id) {
            return;
        }
        this.selectedAssessment = assessment;
        this.newReportSidebarVisible = true;
    }

    onFlightReportDetailsClosed(): void {
        this.newReportSidebarVisible = false;
        this.newReportSidebarService.clearLastRequested();
    }
}
